import { Reveal } from "@/components/Reveal";

const MILESTONES = [
  ["Kickoff", "50%", "$3,750", "On signing, before work starts."],
  ["Foundation complete \u00b7 ~week 4", "30%", "$2,250", "Sandbox test results shared, data model built, and Monthly Budget object built with a test import of the 2026 data \u2014 or the end of week 5, whichever comes first."],
  ["Go-live \u00b7 by week 12", "20%", "$1,500", "On go-live."],
];

const WHO = [
  ["LeadG2", ["All client communication", "Scheduling sessions and chasing decisions", "Sending templates to markets and collecting them", "Coordinating UAT feedback", "Delivering AE training"]],
  ["Prodigitas", ["Sandbox test, data model and all build work", "Template correction, validation and imports", "Custom code, roll-up, reports and dashboards", "QA and reconciliation against WideOrbit", "Training materials, SOPs and documentation", "Weekly status update to LeadG2"]],
  ["Together", ["Week 1 working session", "UAT fix cycles", "Go-live call"]],
];

const PROVIDES = [
  "Filled 2027 templates for all markets, using our corrected version",
  "Confirmation of the account code to category mapping (we draft it)",
  "The station list for each market, with property codes",
  "The owner list, including vacant seats and WideOrbit AE codes",
  "The Paid Programming decision",
];

const NOT_INCLUDED = [
  "Rebuilding 2026 budgets",
  "Changes to the WideOrbit integration",
  "Re-imports after budget figures change. One import per market, plus one correction round from the exceptions report, is included",
  "Year-over-year or historical reporting",
  "Adding line items to deals already open in the pipeline \u2014 we'll provide a template and instructions for MMG's AEs",
  "Support beyond 2 weeks of bug fixes after go-live",
];

const ASSUMPTIONS = [
  "Kickoff date agreed, with sandbox and portal access in week 1",
  "The seven build decisions are closed in the week 1 session, and templates come back by week 3",
  "UAT feedback comes back within 3-4 business days",
  "Market dashboards filter by market. If MMG needs markets restricted from viewing each other's data, we'll confirm the approach after the week 1 sandbox test and quote any additional effort before building it",
  "Hosting outside HubSpot, if it's needed*",
  "Anything outside this scope goes through a short change request, quoted before work starts",
];

const BEYOND = [
  ["Weighted forecasting, configurable %", "Forecast settings record, weights on forecast records, weighted pending and projected in the roll-up"],
  ["Vacant seats and market-level Paid Programming", "Budget owner field independent of HubSpot users, placeholder and market-bucket owners"],
  ["New Business reporting", "Monthly new business totals from the WideOrbit flag, at AE, market and corporate level"],
  ["Category groups", "Broadcast / Digital / Magazine across all three objects, with group-level reports"],
  ["Cash-only pacing", "Cash/trade flag on line items and cash-only filters in the roll-up"],
];

export default function FeeScope() {
  return (
    <section id="fee" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-fee">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">Fee &amp; scope</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">$7,500 fixed fee for the full scope</h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl">
          This covers everything on this page, including the six additional requirements
          and our own delivery project management. Your team runs the client side, my team
          runs delivery.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5" data-testid="fee-milestones">
          <h3 className="font-display text-lg text-coal">Payment milestones</h3>
          <table className="mt-3 w-full text-[14px]" data-testid="fee-milestones-table">
            <tbody>
              {MILESTONES.map(([name, pct, amt, note]) => (
                <tr key={name} className="border-b border-hair last:border-0 align-top">
                  <td className="py-3 pr-4 font-medium text-coal lg:w-[26%]">{name}</td>
                  <td className="py-3 pr-4 font-mono text-fog whitespace-nowrap lg:w-[10%]">{pct}</td>
                  <td className="py-3 pr-4 font-mono text-coal whitespace-nowrap lg:w-[14%]">{amt}</td>
                  <td className="py-3 text-fog leading-relaxed">{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[12.5px] text-fog leading-relaxed">
            Go-live means the budget, forecast and pacing dashboards are live in MMG's
            production HubSpot portal for all markets in scope.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-4 bg-white border border-hair rounded-md p-5" data-testid="fee-who-does-what">
          <h3 className="font-display text-lg text-coal">Who does what</h3>
          <div className="mt-3 grid md:grid-cols-3 gap-4">
            {WHO.map(([col, items]) => (
              <div key={col}>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-fog border-b border-hair pb-2">{col}</p>
                <ul className="mt-2 space-y-1.5">
                  {items.map((it) => (
                    <li key={it} className="text-[13.5px] text-coal/85 leading-relaxed">{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
            These are general guidelines. We're flexible and happy to accommodate
            reasonable requests to keep the project moving.
          </p>
        </div>
      </Reveal>

      <div className="mt-4 grid lg:grid-cols-2 gap-4 items-start">
        <Reveal delay={0.1}>
          <div className="bg-white border border-hair rounded-md p-5 h-full" data-testid="fee-provides">
            <h3 className="font-display text-lg text-coal">What MMG provides by week 3</h3>
            <ul className="mt-3 space-y-1.5">
              {PROVIDES.map((it) => (
                <li key={it} className="flex gap-2.5 text-[14px] text-coal/85 leading-relaxed">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-coal" />
                  {it}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
              Prodigitas fixes the master template, LeadG2 sends it to the markets, MMG
              fills it in, and we validate it and send back an exceptions report. MMG
              corrects its own figures.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="bg-white border border-hair rounded-md p-5 h-full" data-testid="fee-not-included">
            <h3 className="font-display text-lg text-coal">Not included</h3>
            <ul className="mt-3 space-y-1.5">
              {NOT_INCLUDED.map((it) => (
                <li key={it} className="flex gap-2.5 text-[14px] text-coal/85 leading-relaxed">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-fog" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4 items-start">
        <Reveal delay={0.14}>
          <div className="bg-white border border-hair rounded-md p-5 h-full" data-testid="fee-assumptions">
            <h3 className="font-display text-lg text-coal">Assumptions</h3>
            <ul className="mt-3 space-y-1.5">
              {ASSUMPTIONS.map((it) => (
                <li key={it} className="flex gap-2.5 text-[14px] text-coal/85 leading-relaxed">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-fog" />
                  {it}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
              * If the nightly jobs need to run outside HubSpot, Prodigitas will set this
              up as part of the project. The hosting account will be in MMG's (or LeadG2's)
              name, and hosting costs will be paid by the client. Costs for a job of this
              size are typically low.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="bg-white border border-hair rounded-md p-5 h-full" data-testid="fee-beyond">
            <h3 className="font-display text-lg text-coal">Included beyond the change order</h3>
            <div className="mt-3">
              {BEYOND.map(([t, b]) => (
                <div key={t} className="py-2.5 border-b border-hair last:border-0">
                  <p className="text-[14px] font-medium text-coal">{t}</p>
                  <p className="mt-0.5 text-[13px] text-fog leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
